import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { db } from "../firebase";
import { format } from "date-fns";
import { ProfileContext } from "./ProfileContext";
import {
  collectionGroup,
  DocumentData,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  where,
} from "firebase/firestore";
import { WORKFLOW_SCHEDULER_TYPES } from "../functions/postActionTypes";

export const SchedJobContext = createContext<any>({});

const PAST_PAGE_SIZE = 50;

// Map a Firestore doc snapshot to the shared job shape used by all consumers.
function mapDocToSchedJob(docSnap: QueryDocumentSnapshot<DocumentData>): any {
  const d = docSnap.data();
  return {
    JobDocId: d.jobInfo.core.uniqueJobId,
    ShiftIdentifier: d.jobInfo.core.shiftIdentifier || "",
    SchedUid: d.docStamp.userInfoFrom.uid,
    DateDay:
      format(new Date(d.jobInfo.core.dateTimeStart.substring(0, 19)), "M/d/yy") || "",
    EndDay:
      format(new Date(d.jobInfo.core.dateTimeFinish.substring(0, 19)), "M/d/yy") || "",
    StartDateTime:
      format(new Date(d.jobInfo.core.dateTimeStart.substring(0, 19)), "h:mma") || "",
    FinishDateTime:
      format(new Date(d.jobInfo.core.dateTimeFinish.substring(0, 19)), "h:mma") || "",
    StartDateTimeRaw: d.jobInfo.core.dateTimeStart,
    FinishDateTimeRaw: d.jobInfo.core.dateTimeFinish,
    Company: d.jobInfo.core.company,
    Location: d.jobInfo.core.location,
    LocationCoordinates: d.jobInfo.core.locationCoordinates,
    Pay: d.jobInfo.core.pay,
    ShiftNotes: d.jobInfo.core.shiftNotes,
    NumJobSlots: d.jobInfo.core.numJobSlots,
    JobStatusSched: d.jobInfo.status.jobStatusSched,
    JobStatusWerker: d.jobInfo.status.jobStatusWerker,
    IsPaidJob: d.jobInfo.status.isPaidJob,
    PaidWerker: d.jobInfo.status.isPaidWerker,
    PaidWerkers: d.jobInfo.status.isPaidWerkers,
    JobCancelledByScheduler: d.jobInfo.status.jobCancelledByScheduler,
    CreatedAt: d.docStamp.timestamp.createdAt,
    UpdatedAt: d.docStamp.timestamp.updatedAt,
    IsRead: d.jobInfo.status.isRead,
    IsNewMessage: d.jobInfo.status.isNewMessage,
    scheduledCount: d.jobInfo.status.scheduledCount,
    seriesId: (d.jobInfo?.core?.seriesId as string) ?? (d.seriesId as string) ?? null,
    seriesIndex: (d.jobInfo?.core?.seriesIndex as number) ?? (d.seriesIndex as number) ?? null,
    seriesTotal: (d.jobInfo?.core?.seriesTotal as number) ?? (d.seriesTotal as number) ?? null,
  };
}

export const SchedJobContextProvider: React.FC<any> = ({ children }) => {
  const { profile } = useContext(ProfileContext);

  // ── Open + Filled (real-time, no limit) ───────────────────────────────────
  const [openFilledJobs, setOpenFilledJobs] = useState<any[]>([]);
  const [openJobsCount, setOpenJobsCount] = useState(0);
  const [filledJobsCount, setFilledJobsCount] = useState(0);
  const [badgeCountOpenSegmentSchedulerContext, setBadgeCountOpenSegmentSchedulerContext] = useState(0);
  const [badgeCountFilledSegmentSchedulerContext, setBadgeCountFilledSegmentSchedulerContext] = useState(0);
  const [_openFilledHeaderBadge, _setOpenFilledHeaderBadge] = useState(0);

  // ── Past (real-time first page; cursor-paginated beyond) ──────────────────
  const [pastJobs, setPastJobs] = useState<any[]>([]);
  const [pastJobsCount, setPastJobsCount] = useState(0);
  const [badgeCountPastSegmentSchedulerContext, setBadgeCountPastSegmentSchedulerContext] = useState(0);
  const [hasMorePast, setHasMorePast] = useState(false);
  const [earliestPastJobDate, setEarliestPastJobDate] = useState<string>("");
  // Stored in a ref so loadMorePast doesn't recreate on every snapshot update
  const pastCursorRef = useRef<QueryDocumentSnapshot<DocumentData> | null>(null);
  const loadingMoreRef = useRef(false);

  // ── Merged array (backward-compatible for all consumers) ──────────────────
  const schedJob = useMemo(() => [...openFilledJobs, ...pastJobs], [openFilledJobs, pastJobs]);

  // Header toggle badge mirrors original formula:
  //   openNewMsg + filledUnread + filledNewMsg + pastNewMsg
  const badgeCountHeaderToggleSchedulerContext = useMemo(
    () => _openFilledHeaderBadge + badgeCountPastSegmentSchedulerContext,
    [_openFilledHeaderBadge, badgeCountPastSegmentSchedulerContext]
  );

  // ── Listener A: Open + Filled ─────────────────────────────────────────────
  // Requires composite index: (userInfoFrom.uid, userInfoTo.uid, jobStatusSched, dateTimeStart)
  useEffect(() => {
    const uid = profile?.UserId;
    if (!uid) return;

    const q = query(
      collectionGroup(db, "jobsDocs"),
      where("docStamp.userInfoFrom.uid", "==", uid),
      where("docStamp.userInfoTo.uid", "==", uid),
      where("jobInfo.status.jobStatusSched", "in", [
        WORKFLOW_SCHEDULER_TYPES.Open,
        WORKFLOW_SCHEDULER_TYPES.Filled,
      ]),
      orderBy("jobInfo.core.dateTimeStart", "desc")
    );

    const unsub = onSnapshot(q, (snap) => {
      const docs: any[] = [];
      let badgeOpen = 0, badgeOpenMsg = 0, badgeFilled = 0, badgeFilledMsg = 0;
      let tempOpen = 0, tempFilled = 0;

      snap.forEach((docSnap) => {
        try {
          const job = mapDocToSchedJob(docSnap);
          docs.push(job);
          if (job.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Open) {
            tempOpen++;
            if (job.IsRead === false) badgeOpen++;
            if (job.IsNewMessage === true) badgeOpenMsg++;
          } else if (job.JobStatusSched === WORKFLOW_SCHEDULER_TYPES.Filled) {
            tempFilled++;
            if (job.IsRead === false) badgeFilled++;
            if (job.IsNewMessage === true) badgeFilledMsg++;
          }
        } catch {
          // skip malformed doc
        }
      });

      setOpenFilledJobs(docs);
      setOpenJobsCount(tempOpen);
      setFilledJobsCount(tempFilled);
      setBadgeCountOpenSegmentSchedulerContext(badgeOpen + badgeOpenMsg);
      setBadgeCountFilledSegmentSchedulerContext(badgeFilled + badgeFilledMsg);
      // openMsg + filledUnread + filledMsg (mirrors original header toggle formula)
      _setOpenFilledHeaderBadge(badgeOpenMsg + badgeFilled + badgeFilledMsg);
    });

    return () => unsub();
  }, [profile?.UserId]);

  // ── Listener B: Past — first PAST_PAGE_SIZE docs ──────────────────────────
  // Requires composite index: (userInfoFrom.uid, userInfoTo.uid, jobStatusSched, dateTimeStart)
  useEffect(() => {
    const uid = profile?.UserId;
    if (!uid) return;

    // Reset pagination state when user changes
    pastCursorRef.current = null;
    setHasMorePast(false);
    setEarliestPastJobDate("");

    const q = query(
      collectionGroup(db, "jobsDocs"),
      where("docStamp.userInfoFrom.uid", "==", uid),
      where("docStamp.userInfoTo.uid", "==", uid),
      where("jobInfo.status.jobStatusSched", "==", WORKFLOW_SCHEDULER_TYPES.Past),
      orderBy("jobInfo.core.dateTimeStart", "desc"),
      limit(PAST_PAGE_SIZE)
    );

    const unsub = onSnapshot(q, (snap) => {
      const docs: any[] = [];
      let badgePastMsg = 0;
      let lastSnap: QueryDocumentSnapshot<DocumentData> | null = null;

      snap.forEach((docSnap) => {
        try {
          const job = mapDocToSchedJob(docSnap);
          docs.push(job);
          if (job.IsNewMessage === true) badgePastMsg++;
          lastSnap = docSnap;
        } catch {
          // skip malformed doc
        }
      });

      setPastJobs(docs);
      setPastJobsCount(docs.length);
      setBadgeCountPastSegmentSchedulerContext(badgePastMsg);
      pastCursorRef.current = lastSnap;
      setHasMorePast(snap.size === PAST_PAGE_SIZE);
      if (docs.length > 0) {
        setEarliestPastJobDate(docs[docs.length - 1].StartDateTimeRaw || "");
      }
    });

    return () => unsub();
  }, [profile?.UserId]);

  // ── Load next page of past jobs (cursor pagination) ───────────────────────
  const loadMorePast = useCallback(async () => {
    const uid = profile?.UserId;
    if (!uid || !pastCursorRef.current || !hasMorePast || loadingMoreRef.current) return;

    loadingMoreRef.current = true;
    try {
      const q = query(
        collectionGroup(db, "jobsDocs"),
        where("docStamp.userInfoFrom.uid", "==", uid),
        where("docStamp.userInfoTo.uid", "==", uid),
        where("jobInfo.status.jobStatusSched", "==", WORKFLOW_SCHEDULER_TYPES.Past),
        orderBy("jobInfo.core.dateTimeStart", "desc"),
        startAfter(pastCursorRef.current),
        limit(PAST_PAGE_SIZE)
      );

      const snap = await getDocs(q);
      const newDocs: any[] = [];
      let lastSnap: QueryDocumentSnapshot<DocumentData> | null = null;

      snap.forEach((docSnap) => {
        try {
          newDocs.push(mapDocToSchedJob(docSnap));
          lastSnap = docSnap;
        } catch {
          // skip malformed doc
        }
      });

      if (newDocs.length > 0) {
        setPastJobs((prev) => [...prev, ...newDocs]);
        setPastJobsCount((prev) => prev + newDocs.length);
        pastCursorRef.current = lastSnap;
        setEarliestPastJobDate(newDocs[newDocs.length - 1].StartDateTimeRaw || "");
      }
      setHasMorePast(snap.size === PAST_PAGE_SIZE);
    } catch {
      // no-op
    } finally {
      loadingMoreRef.current = false;
    }
  }, [profile?.UserId, hasMorePast]);

  const schedContextValue = useMemo(
    () => ({
      schedJob,
      openJobsCount,
      filledJobsCount,
      pastJobsCount,
      badgeCountOpenSegmentSchedulerContext,
      badgeCountFilledSegmentSchedulerContext,
      badgeCountPastSegmentSchedulerContext,
      badgeCountHeaderToggleSchedulerContext,
      hasMorePast,
      earliestPastJobDate,
      loadMorePast,
    }),
    [
      schedJob,
      openJobsCount,
      filledJobsCount,
      pastJobsCount,
      badgeCountOpenSegmentSchedulerContext,
      badgeCountFilledSegmentSchedulerContext,
      badgeCountPastSegmentSchedulerContext,
      badgeCountHeaderToggleSchedulerContext,
      hasMorePast,
      earliestPastJobDate,
      loadMorePast,
    ]
  );

  return (
    <SchedJobContext.Provider value={schedContextValue}>
      {children}
    </SchedJobContext.Provider>
  );
};
